import { matchRoutes } from 'react-router-dom'

import { isB2bPortalPath, ROUTES, type RoutePageType } from '@/routes/paths'

// Page types that have NO route of their own — they're fired manually via the
// escape hatch (analytics.trackPageView) for surfaces with no distinct URL:
// - mcp_connect_sign_in: the /mcp-connect entry, a second HTML document with no router.
// - mcp_promo: the /mcp-promo entry, a third one. One page type, not one per connector —
//   which host the visitor came from rides on every event as the `connector` property
//   (see `mcpPromoAnalytics.ts`), so adding a connector never adds a page type.
// - b2b_practice: the practice view of the B2B simulation page (`b2b_simulation`). The events the view sends itself
//   (start_call, call started, call ended) keep it; paused/resumed and the microphone setup take the route's.
// (The post-call feedback phase reuses the `scenario_feedback` route page type.)
export const VIRTUAL_PAGE_TYPES = ['mcp_connect_sign_in', 'mcp_promo', 'b2b_practice'] as const
export type VirtualPageType = (typeof VIRTUAL_PAGE_TYPES)[number]

// The full set of page types: every route's pageType (derived from the route table)
// plus the virtual ones. This is the type used across the analytics event catalog.
export type PageType = RoutePageType | VirtualPageType

// A route leaf flattened for matchRoutes: absolute `fullPath` pattern + its pageType,
// plus any optional route metadata consumers read at runtime (e.g. `keepScreenAwake`).
type FlatRoute = { path: string; pageType: RoutePageType; keepScreenAwake?: boolean }

function isRouteLeaf(node: unknown): node is { fullPath: string; pageType: RoutePageType; keepScreenAwake?: boolean } {
  return typeof node === 'object' && node !== null && 'fullPath' in node && 'pageType' in node
}

// Walk ROUTES (and its nested groups, AUTH and B2B) into a flat pattern list. Built
// once at module load; uses `fullPath` (absolute) because that is what a real
// pathname matches against — the router's own `path` values are relative.
function flattenRoutes(tree: object): FlatRoute[] {
  const out: FlatRoute[] = []
  for (const node of Object.values(tree)) {
    if (isRouteLeaf(node)) {
      out.push({ path: node.fullPath, pageType: node.pageType, keepScreenAwake: node.keepScreenAwake })
    } else if (node && typeof node === 'object') {
      out.push(...flattenRoutes(node)) // nested group (AUTH, B2B)
    }
  }
  return out
}

/** Every route leaf of the table, by absolute pattern. */
export const FLAT_ROUTES = flattenRoutes(ROUTES)

export type ResolvedPage = {
  pageType?: RoutePageType
  params: Record<string, string | undefined>
  // Always resolved to a concrete boolean (defaults to false for routes that don't opt in),
  // so consumers can use it directly without an `=== true` guard.
  keepScreenAwake: boolean
}

// Resolve a pathname to its route pageType + path params (+ optional route metadata such
// as `keepScreenAwake`). Pure and router-context-free (reads only its argument) so both
// the enrichment plugin (which runs before any React renders, on session_start) and the
// RouteAnalytics / ScreenWakeLockManager components can share it. Uses matchRoutes rather than
// a matchPath loop, which ranks a static pattern above an overlapping dynamic one regardless of
// definition order. No route pair depends on that today — the table has no static segment that
// collides with a dynamic one — so nothing pins the choice; it is what keeps a future static
// sibling of e.g. `.../scenario/:scenarioId` from being shadowed by definition order.
export function resolvePathname(pathname: string): ResolvedPage {
  const matches = matchRoutes(FLAT_ROUTES, pathname)
  const leaf = matches?.[matches.length - 1]
  if (!leaf) return { params: {}, keepScreenAwake: false }
  const route = leaf.route
  return {
    pageType: route.pageType,
    params: leaf.params,
    keepScreenAwake: route.keepScreenAwake ?? false,
  }
}

type LegacyIds = {
  workspace_id?: string
  scenario_id?: string
  session_id?: string
  template_id?: string
}

// Map matchRoutes' param names to the legacy snake_case keys existing Amplitude charts
// are built on. Only present keys are emitted (flat, top-level).
export function mapParamsToLegacyKeys(params: Record<string, string | undefined>): LegacyIds {
  const ids: LegacyIds = {}
  if (typeof params.id !== 'undefined') ids.workspace_id = params.id
  if (typeof params.scenarioId !== 'undefined') ids.scenario_id = params.scenarioId
  if (typeof params.sessionId !== 'undefined') ids.session_id = params.sessionId
  if (typeof params.templateId !== 'undefined') ids.template_id = params.templateId
  return ids
}

/** Which portal an event comes from: the NoPressure B2B one under `/work`, else the original one. */
export type FlowOrigin = 'original' | 'b2b'

type RouteContext = LegacyIds & { b2b_session_id?: string; flow_origin: FlowOrigin }

/**
 * What the route adds to every SPA event: its portal, and its ids. The original portal's go under the legacy keys;
 * a `/work` route's are the B2B portal's own, so none goes out as an original id, and its session is `b2b_session_id`.
 */
export function routeContextOf(pathname: string, params: Record<string, string | undefined>): RouteContext {
  if (!isB2bPortalPath(pathname)) return { ...mapParamsToLegacyKeys(params), flow_origin: 'original' }
  return {
    ...(typeof params.b2bSessionId !== 'undefined' ? { b2b_session_id: params.b2bSessionId } : {}),
    flow_origin: 'b2b',
  }
}

// Page types whose page_viewed event should NOT be emitted. Blocks page views only —
// interaction events (clicks, feedback) on these pages still flow with their
// pageType. `satisfies readonly PageType[]` prevents an entry from drifting from a real
// page type. Seeded with the redirect/layout + transient consume pages.
export const PAGE_VIEW_BLACKLIST = [
  'app_root', // '/' → immediate redirect
  'auth_root', // '/auth' → layout/redirect wrapper
  'workspace_detail', // '/workspaces/:id' → not registered in router.tsx; catch-all redirects to library
  'invite', // transient invite-token consume + redirect
  'clone_workspace', // transient clone action page
  'clone_workspace_scenario', // transient clone action page
  'regenerate_feedback', // debug-only feedback-regeneration page
] as const satisfies readonly PageType[]

export function isPageViewBlacklisted(pageType: PageType): boolean {
  return (PAGE_VIEW_BLACKLIST as readonly PageType[]).includes(pageType)
}
